// Roster CSV parsing for facility user import.
//
// The original was `line.split(",")` with a validity test of "contains @ and
// no space". That silently lost whole rosters:
//
//   - a name-first column order dropped EVERY row (nothing in cell 0 had an @)
//   - `"Lee, Ann",a@x.com` split mid-name and dropped the row
//   - a header of "Email Address" was imported as data, then dropped
//   - semicolon-delimited files (EU Excel) were WORSE than dropped: the whole
//     line became one cell, `a@x.com;Ann;Lee` passed the @-check, and a
//     corrupt address was imported and failed later at the provider
//
// So: a real quoted-field parser, delimiter sniffing, header matching by NAME
// rather than position, and a reason on every rejected row so the admin can
// see what happened instead of wondering where 310 people went.

export const ROLES = ["viewer", "user", "admin"];
const DELIMITERS = [",", ";", "\t", "|"];

// Header aliases, normalised to letters only ("E-mail Address" -> "emailaddress")
const FIELD_ALIASES = {
  email: ["email", "emailaddress", "mail", "mailaddress", "useremail",
          "workemail", "emails"],
  first: ["first", "firstname", "givenname", "given", "fname", "forename"],
  last: ["last", "lastname", "surname", "familyname", "family", "lname"],
  role: ["role", "accessrole", "permission", "permissions", "usertype",
         "type", "level"],
  name: ["name", "fullname", "displayname", "contact"],
};

const normalizeKey = (value) =>
  String(value || "").toLowerCase().replace(/[^a-z]/g, "");

/** RFC4180-ish scan: honours quoted fields, "" escapes and quoted newlines. */
export const parseDelimited = (text, delimiter) => {
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  let started = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      started = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
      started = true;
    } else if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      started = false;
    } else if (ch !== "\r") {
      field += ch;
      started = true;
    }
  }
  if (started || field || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ""));
};

/** Pick the delimiter that yields the most columns, consistently. */
export const sniffDelimiter = (text) => {
  let best = { delimiter: ",", score: -1 };
  DELIMITERS.forEach((delimiter) => {
    const rows = parseDelimited(text, delimiter);
    if (!rows.length) return;
    const counts = rows.slice(0, 20).map((r) => r.length);
    const columns = Math.max(...counts);
    if (columns < 2) return;
    // Consistent column counts beat merely many columns — a stray comma in
    // one field shouldn't win over the delimiter that parses every row.
    const consistent = counts.filter((c) => c === columns).length / counts.length;
    const score = columns + consistent * 2;
    if (score > best.score) best = { delimiter, score };
  });
  return best.delimiter;
};

/** Map a header row to field indexes, or null when it isn't a header. */
export const mapHeader = (cells) => {
  const mapping = {};
  cells.forEach((cell, index) => {
    const key = normalizeKey(cell);
    if (!key) return;
    Object.entries(FIELD_ALIASES).forEach(([field, aliases]) => {
      if (mapping[field] === undefined && aliases.includes(key)) {
        mapping[field] = index;
      }
    });
  });
  // A header is only a header if it names the one column we cannot guess.
  return mapping.email === undefined && mapping.name === undefined
    ? null
    : mapping;
};

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[a-z]{2,}$/i;

const splitName = (full) => {
  const parts = String(full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { first: "", last: "" };
  if (parts.length === 1) return { first: parts[0], last: "" };
  return { first: parts.slice(0, -1).join(" "), last: parts[parts.length - 1] };
};

const normalizeRole = (value) => {
  const role = String(value || "").trim().toLowerCase();
  return ROLES.includes(role) ? role : "user";
};

/**
 * Parse a roster CSV into rows the import endpoint accepts.
 *
 * Every row comes back either usable or with a `reason` — nothing is dropped
 * without saying why. Duplicate addresses are flagged rather than sent twice,
 * because the endpoint would treat the second as an existing user and the
 * count would quietly disagree with the file.
 */
export const parseRosterCsv = (text) => {
  const raw = String(text || "").replace(/^﻿/, ""); // Excel BOM
  if (!raw.trim()) return [];

  const delimiter = sniffDelimiter(raw);
  const rows = parseDelimited(raw, delimiter);
  if (!rows.length) return [];

  const header = mapHeader(rows[0]);
  const body = header ? rows.slice(1) : rows;
  // No recognisable header: fall back to the documented positional order.
  const mapping = header || { email: 0, first: 1, last: 2, role: 3 };

  const seen = new Set();
  return body.map((cells, index) => {
    const at = (field) =>
      mapping[field] === undefined ? "" : String(cells[mapping[field]] ?? "").trim();

    const email = at("email");
    let first = at("first");
    let last = at("last");
    if (mapping.name !== undefined && !first && !last) {
      ({ first, last } = splitName(at("name")));
    }

    const row = {
      line: index + (header ? 2 : 1), // 1-based, counting the header
      email,
      first,
      last,
      role: normalizeRole(at("role")),
      valid: false,
      reason: "",
    };

    if (!email) {
      row.reason = "no email address in this row";
      return row;
    }
    if (!EMAIL_RE.test(email)) {
      // The delimiter case lands here: "a@x.com;Ann;Lee" is rejected instead
      // of being imported as a working-looking address.
      row.reason = `"${email}" is not a valid email address`;
      return row;
    }
    const key = email.toLowerCase();
    if (seen.has(key)) {
      row.reason = "duplicate of an earlier row";
      return row;
    }
    seen.add(key);
    row.valid = true;
    return row;
  });
};

/** Short description of how the file was read, for the import panel. */
export const describeParse = (text) => {
  const raw = String(text || "").replace(/^﻿/, "");
  if (!raw.trim()) return null;
  const delimiter = sniffDelimiter(raw);
  const rows = parseDelimited(raw, delimiter);
  const header = rows.length ? mapHeader(rows[0]) : null;
  const names = { ",": "comma", ";": "semicolon", "\t": "tab", "|": "pipe" };
  return {
    delimiter: names[delimiter] || delimiter,
    header: Boolean(header),
    columns: header
      ? Object.keys(header).filter((k) => header[k] !== undefined)
      : ["email", "first", "last", "role"],
  };
};
